/**
 * On-prem desktop runtime server configuration.
 *
 * The Tauri desktop app has no build-time knowledge of the on-prem server
 * URL (unlike the Docker/web build which bakes it via NEXT_PUBLIC_* ARGs
 * + onprem-runtime-config.js placeholder replacement). Instead, the user
 * enters the server URL on first launch, and it is persisted in
 * `localStorage` so it survives app restarts.
 *
 * ## Status — write side only
 *
 * This module is the first tranche: only the write side is wired.
 * `/onprem/server-setup` calls `setOnPremServerUrl` / `clearOnPremServerUrl`,
 * and the read-side getters below have no consumers yet. `oidcClient.ts`
 * still builds its authority from the build-time `KEYCLOAK_ISSUER_URL`
 * (`src/utils/keycloakConfig.ts`) and its `redirect_uri` from
 * `window.location.origin`, so a URL stored here does not yet influence
 * auth, API, or WebSocket traffic.
 *
 * Two build-time couplings must be resolved before the getters can drive
 * anything, both tracked on the follow-up tranches:
 *
 * - The Keycloak issuer is inlined at build time and fail-fast validated at
 *   module load, so a runtime-entered origin cannot reach the OIDC client
 *   without reworking that seam.
 * - On-prem `connect-src` is generated at build time from
 *   `NEXT_PUBLIC_API_BASE_URL` / `NEXT_PUBLIC_WS_BASE_URL` (see
 *   `config/csp/onprem-csp.js`). That CSP is delivered as a Next response
 *   header, so it does not apply to the Tauri `output: 'export'` build
 *   (`tauri.conf.onprem.json` sets `"csp": null`), but it does bind the
 *   on-prem web build.
 *
 * ## Flow
 *
 * 1. First launch: `getOnPremServerUrl()` returns `null`.
 *    The app redirects to `/onprem/server-setup` where the user enters
 *    the server URL (e.g. `https://hospital.local`).
 * 2. `setOnPremServerUrl(url)` validates + stores the URL.
 * 3. On subsequent launches, `getOnPremServerUrl()` returns the stored URL.
 * 4. `getKeycloakIssuerUrl()` derives the Keycloak issuer URL from the
 *    server URL + the realm path (`/realms/heidi-scribe`).
 * 5. `getOnPremApiBaseUrl()` returns the server URL for API calls
 *    (replaces Tauri's internal WebView origin, which is not the actual
 *    server).
 *
 * ## Keycloak redirect URI
 *
 * The OIDC client uses the realm's registered `https://localhost` redirect.
 * Tauri serves bundled assets from a platform-specific internal origin, so
 * the on-prem native navigation plugin maps the allowed Keycloak callback
 * back into the app before the callback page loads.
 */

const STORAGE_KEY = 'onprem_server_url';
const KEYCLOAK_REALM_PATH = '/realms/heidi-scribe';

/**
 * Get the stored on-prem server URL, or null if not yet configured.
 *
 * Browser-only — returns null during SSR (Tauri static export has no SSR,
 * but the guard prevents crashes during `next build` SSG).
 */
export function getOnPremServerUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

/**
 * Validate and store the on-prem server URL, returning the stored origin.
 *
 * Throws on invalid URL (missing protocol, non-HTTPS for non-local hosts,
 * or a path/query/fragment component — see below).
 *
 * Only the origin is stored, so a URL carrying anything below the root
 * would be silently truncated and every derived URL (issuer, API, WS)
 * would point somewhere the user did not ask for. Reject instead of
 * truncating: reverse-proxy subpath deployments are not supported, and a
 * loud error beats a wrong origin that still passes a health check.
 */
export function setOnPremServerUrl(url: string): string {
  if (typeof window === 'undefined') {
    throw new Error('setOnPremServerUrl requires a browser environment.');
  }

  const trimmed = url.trim().replace(/\/+$/, ''); // strip trailing slashes
  if (!trimmed) {
    throw new Error('Server URL cannot be empty.');
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error(
      `Invalid URL: ${trimmed}. Include the protocol (e.g. https://hospital.local).`
    );
  }

  // Enforce HTTPS for non-local hosts (air-gap deployments use self-signed
  // CA, so HTTPS is the norm). Allow HTTP only for localhost / loopback
  // (dev testing). `URL.hostname` returns IPv6 literals in bracketed form
  // (`[::1]`), so the loopback check strips brackets before comparing.
  const hostname = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, '');
  const isLocal =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost');

  if (!isLocal && parsed.protocol !== 'https:') {
    throw new Error(
      `Server URL must use HTTPS for non-local hosts (got ${parsed.protocol}//). ` +
        'On-prem deployments use a self-signed CA with TLS.'
    );
  }

  if (parsed.pathname !== '/' || parsed.search || parsed.hash) {
    throw new Error(
      `Server URL must not include a path, query, or fragment (got ` +
        `${parsed.pathname}${parsed.search}${parsed.hash}). Enter the server ` +
        'root only, e.g. https://hospital.local.'
    );
  }

  window.localStorage.setItem(STORAGE_KEY, parsed.origin);
  return parsed.origin;
}

/**
 * Clear the stored server URL (for "change server" / re-configuration).
 */
export function clearOnPremServerUrl(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Derive the Keycloak issuer URL from the stored server URL.
 *
 * Returns `null` if the server URL is not yet configured — callers should
 * redirect to the server-setup page in that case.
 *
 * The realm path is hardcoded to `/realms/heidi-scribe` (matching the
 * realm name in deploy-app.sh). If multi-realm support is ever needed,
 * this can be extended to read from a separate localStorage key.
 */
export function getKeycloakIssuerUrl(): string | null {
  const serverUrl = getOnPremServerUrl();
  if (!serverUrl) return null;
  return `${serverUrl}${KEYCLOAK_REALM_PATH}`;
}

/**
 * Get the API base URL for on-prem desktop.
 *
 * In the web build, API calls use `window.location.origin` (which is the
 * K8s ingress URL). In the Tauri build, `window.location.origin` is
 * an internal app origin, so we must use the stored server URL instead.
 *
 * Returns `null` if not yet configured.
 */
export function getOnPremApiBaseUrl(): string | null {
  return getOnPremServerUrl();
}

/**
 * Get the WebSocket base URL for on-prem desktop.
 *
 * Derives ws/wss protocol from the server URL protocol.
 * Returns `null` if not yet configured.
 */
export function getOnPremWebSocketUrl(): string | null {
  const serverUrl = getOnPremServerUrl();
  if (!serverUrl) return null;
  try {
    const parsed = new URL(serverUrl);
    const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${parsed.host}`;
  } catch {
    return null;
  }
}

/**
 * Check whether the on-prem server URL has been configured.
 * Convenience wrapper for gating UI (e.g. showing setup page vs login).
 */
export function isOnPremServerConfigured(): boolean {
  return getOnPremServerUrl() !== null;
}
